import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import adminApi, { errMsg } from '../../api';
import { useAdminAuth, hasRole } from '../../auth';
import { useAttention } from '../../attention';
import { Band } from '../../components/Card';
import DataTable from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import JobSummaryPanel from '../../components/JobSummaryPanel';
import PageHeader from '../../components/PageHeader';
import Pagination from '../../components/Pagination';
import StatusPill from '../../components/StatusPill';
import { ConfirmDialog } from '../../components/Dialog';
import { formatCompactNumber, formatDateTime, formatRelative } from '../../format';
import useSort, { applySort } from '../../hooks/useSort';

/**
 * Catalog › Crawl — the cross-brand view.
 *
 * A single brand's crawl status lives on that brand's own page, which is
 * where you look when you already know which brand you care about. This page
 * exists for the other question, the one the product doc calls out
 * specifically: "a cross-brand view for spotting a systemic crawler issue
 * (several brands stale at once, a shared failure signature) rather than
 * only ever looking one brand at a time."
 *
 * So the summary band leads with how many sources are failing or stale, not
 * with a list — several brands breaking simultaneously almost never means
 * several brands changed their markup on the same night. The summary tiles
 * are clickable: clicking "Failing" filters the per-source table instantly
 * so you can find which brand(s) need attention without scrolling.
 *
 * Sources are paginated (they number in the hundreds) and search-filtered
 * client-side. The jobs table is capped server-side.
 */
const IN_FLIGHT = ['queued', 'running'];

function matches(query, ...fields) {
  if (!query) return true;
  const q = query.toLowerCase();
  return fields.some((f) => f && String(f).toLowerCase().includes(q));
}

export default function CrawlPage() {
  const { admin } = useAdminAuth();
  const { refresh: refreshAttention } = useAttention();
  const canTrigger = hasRole(admin, 'operator', 'owner');
  const [params, setParams] = useSearchParams();

  const [status, setStatus] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [cooldown, setCooldown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState({});
  const [forceOpen, setForceOpen] = useState(false);
  const [sourceQuery, setSourceQuery] = useState('');
  const [jobQuery, setJobQuery] = useState('');
  const [fallbackJob, setFallbackJob] = useState(null);
  const [fallbackLoading, setFallbackLoading] = useState(false);
  // Sources table: pagination + optional status filter (set by clicking a summary tile)
  const [sourcePage, setSourcePage] = useState(1);
  const [sourcePageSize, setSourcePageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState(null); // null | 'failed' | 'never' | 'running'
  const pollRef = useRef(null);

  const { sort: sourceSort, onSort: onSourceSort } = useSort();
  const { sort: jobSort, onSort: onJobSort } = useSort();

  // Declared before sortedStatus below, which references it in a useMemo
  // dependency array evaluated eagerly — a `const` read before its own
  // declaration throws at runtime, it is not hoisted like a `function`/`var`
  // would be. triggerOne/openJob are only reached from click handlers here
  // (defined further down), which is fine: those only run after the whole
  // component body — and therefore both consts — have already executed once.
  const statusColumns = useMemo(() => [
    {
      key: 'name',
      label: 'Source',
      sortable: true,
      sortValue: (s) => s.name,
      render: (s) => (
        <div className="min-w-0">
          {s.brand_id ? (
            <Link
              to={`/brand/${encodeURIComponent(s.brand_id)}`}
              className="font-medium text-ink hover:text-raspberry-600"
              onClick={(e) => e.stopPropagation()}
            >
              {s.name}
            </Link>
          ) : (
            <span className="font-medium text-ink">{s.name}</span>
          )}
          <div className="truncate font-mono text-[10px] text-ink-muted">{s.source_id}</div>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Last run',
      sortable: true,
      sortValue: (s) => s.last_job?.status ?? '',
      render: (s) => (s.last_job
        ? <StatusPill status={s.last_job.status} />
        : <span className="text-[11px] text-ink-muted">Never run</span>),
    },
    {
      key: 'when',
      label: 'When',
      sortable: true,
      sortValue: (s) => s.last_job?.created_at ?? '',
      render: (s) => (s.last_job
        ? <span title={formatDateTime(s.last_job.created_at)}>{formatRelative(s.last_job.created_at)}</span>
        : null),
    },
    {
      key: 'result',
      label: 'Saved / deactivated',
      align: 'right',
      sortable: true,
      sortValue: (s) => s.last_job?.result?.saved ?? -1,
      render: (s) => (s.last_job?.result
        ? `${formatCompactNumber(s.last_job.result.saved ?? 0)} / ${formatCompactNumber(s.last_job.result.deactivated ?? 0)}`
        : null),
    },
    {
      key: 'enabled',
      label: 'Schedule',
      render: (s) => (s.enabled
        ? <span className="text-[11px] text-ink-secondary">Nightly</span>
        : <StatusPill tone="neutral" label="Disabled" />),
    },
    ...(canTrigger ? [{
      key: 'action',
      label: '',
      align: 'right',
      render: (s) => {
        const busy = IN_FLIGHT.includes(s.last_job?.status);
        return (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); triggerOne(s.source_id); }}
            disabled={triggering[s.source_id] || busy}
            className="rounded-md border border-hairline px-2 py-1 text-[11px] font-medium text-ink-secondary hover:bg-surface-page hover:text-ink disabled:opacity-40"
          >
            {busy ? 'In progress…' : 'Crawl now'}
          </button>
        );
      },
    }] : []),
    // Opening the summary from this row means "the last run for this
    // source", which is the same job the row already displays a status for —
    // a second way in, not a second concept.
    {
      key: 'open',
      label: '',
      align: 'right',
      render: (s) => (s.last_job
        ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openJob(s.last_job.job_id); }}
            className="text-[11px] font-medium text-raspberry-600 hover:text-raspberry-700"
          >
            Summary →
          </button>
        )
        : null),
    },
  ], [canTrigger, triggering]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    try {
      const [statusResp, jobsResp, cooldownResp] = await Promise.all([
        adminApi.get('/ops/crawl/status'),
        adminApi.get('/ops/crawl/jobs', { params: { limit: 100 } }),
        adminApi.get('/ops/crawl/force-all/cooldown'),
      ]);
      setStatus(statusResp.data.items || []);
      setJobs(jobsResp.data.items || []);
      setCooldown(cooldownResp.data);
    } catch (err) {
      toast.error(errMsg(err, 'Could not load crawl status'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Jobs finish off the request path (SQS), so the panel polls rather than
    // asking someone to refresh to watch queued → running → done. This also
    // means an open job summary updates on its own: it's derived from `jobs`
    // below, which this refetches.
    pollRef.current = setInterval(load, 8000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const triggerOne = async (sourceId) => {
    setTriggering((prev) => ({ ...prev, [sourceId]: true }));
    try {
      await adminApi.post('/ops/crawl/trigger', null, { params: { source_id: sourceId } });
      toast.success('Crawl queued');
      await load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not trigger crawl'));
    } finally {
      setTriggering((prev) => ({ ...prev, [sourceId]: false }));
    }
  };

  const forceAll = async () => {
    try {
      const { data } = await adminApi.post('/ops/crawl/force-all', { confirm: true });
      toast.success(`Force-all queued: ${data.jobs_queued} source${data.jobs_queued === 1 ? '' : 's'}`);
      setForceOpen(false);
      await Promise.all([load(), refreshAttention()]);
    } catch (err) {
      toast.error(errMsg(err, 'Could not trigger force-all'));
      setForceOpen(false);
    }
  };

  const summary = useMemo(() => {
    const failing = status.filter((s) => s.last_job?.status === 'failed');
    const never = status.filter((s) => !s.last_job);
    const running = status.filter((s) => IN_FLIGHT.includes(s.last_job?.status));
    return { failing, never, running, total: status.length };
  }, [status]);

  // source_id -> {name, brand_id}, so the job table (which only carries a raw
  // source_id) can be searched and displayed by brand name too, and so the
  // job summary panel can link back to the brand without a second request.
  const sourceById = useMemo(() => {
    const map = new Map();
    status.forEach((s) => map.set(s.source_id, { name: s.name, brand_id: s.brand_id }));
    return map;
  }, [status]);

  // Declared right after sourceById (its only dependency) and before
  // sortedJobs below, which references it in an eagerly-evaluated useMemo
  // dependency array — same reasoning as statusColumns above.
  const jobColumns = useMemo(() => [
    { key: 'when', label: 'When', sortable: true, sortValue: (j) => j.created_at, render: (j) => <span title={formatDateTime(j.created_at)}>{formatRelative(j.created_at)}</span> },
    {
      key: 'source_id',
      label: 'Source',
      sortable: true,
      sortValue: (j) => sourceById.get(j.source_id)?.name || j.source_id,
      render: (j) => {
        const src = sourceById.get(j.source_id);
        return (
          <div className="min-w-0">
            <div className="truncate text-ink">{src?.name || j.source_id}</div>
            <div className="truncate font-mono text-[10px] text-ink-muted">{j.source_id}</div>
          </div>
        );
      },
    },
    {
      key: 'kind',
      label: 'Trigger',
      render: (j) => (j.kind === 'force_all_member'
        ? <StatusPill tone="info" label="Force-all" />
        : <span className="text-[11px]">{j.trigger === 'manual' ? 'Manual' : 'Scheduled'}</span>),
    },
    { key: 'triggered_by', label: 'By', render: (j) => <span className="font-mono text-[10px]">{j.triggered_by}</span> },
    { key: 'status', label: 'Status', sortable: true, render: (j) => <StatusPill status={j.status} /> },
    {
      key: 'result',
      label: 'Saved',
      align: 'right',
      sortable: true,
      sortValue: (j) => j.result?.saved ?? -1,
      render: (j) => (j.result ? formatCompactNumber(j.result.saved ?? 0) : null),
    },
    {
      key: 'error',
      label: 'Error',
      render: (j) => (j.error
        ? <span className="text-status-critical" title={j.error}>{j.error.slice(0, 40)}{j.error.length > 40 ? '…' : ''}</span>
        : null),
    },
  ], [sourceById]);

  // Reset to page 1 whenever the search query or status filter changes.
  useEffect(() => setSourcePage(1), [sourceQuery, statusFilter, sourceSort]);

  const filteredStatus = useMemo(() => {
    let rows = status.filter((s) => matches(sourceQuery, s.name, s.source_id, s.brand_id));
    if (statusFilter === 'failed') rows = rows.filter((s) => s.last_job?.status === 'failed');
    else if (statusFilter === 'never') rows = rows.filter((s) => !s.last_job);
    else if (statusFilter === 'running') rows = rows.filter((s) => IN_FLIGHT.includes(s.last_job?.status));
    return rows;
  }, [status, sourceQuery, statusFilter]);

  const sortedStatus = useMemo(
    () => applySort(filteredStatus, sourceSort, statusColumns),
    [filteredStatus, sourceSort, statusColumns],
  );

  const visibleStatus = useMemo(() => {
    const start = (sourcePage - 1) * sourcePageSize;
    return sortedStatus.slice(start, start + sourcePageSize);
  }, [sortedStatus, sourcePage, sourcePageSize]);

  const sourcePaginationMeta = useMemo(() => ({
    page: sourcePage,
    page_size: sourcePageSize,
    total: sortedStatus.length,
    total_pages: Math.ceil(sortedStatus.length / sourcePageSize),
  }), [sortedStatus.length, sourcePage, sourcePageSize]);

  const visibleJobs = useMemo(
    () => jobs.filter((j) => {
      const src = sourceById.get(j.source_id);
      return matches(jobQuery, src?.name, src?.brand_id, j.source_id, j.job_id, j.triggered_by);
    }),
    [jobs, jobQuery, sourceById],
  );

  const sortedJobs = useMemo(
    () => applySort(visibleJobs, jobSort, jobColumns),
    [visibleJobs, jobSort, jobColumns],
  );

  // The job summary — opened from a row already in memory (instant, no
  // request) or, for a deep link to a job outside the 40 currently loaded,
  // fetched once via GET /ops/crawl/jobs/{id} and enriched the same way.
  const selectedJobId = params.get('job') || '';
  const selectedJob = useMemo(() => {
    if (!selectedJobId) return null;
    const inMemory = jobs.find((j) => j.job_id === selectedJobId);
    if (inMemory) {
      const src = sourceById.get(inMemory.source_id) || {};
      return { ...inMemory, source_name: src.name, brand_id: src.brand_id };
    }
    return fallbackJob && fallbackJob.job_id === selectedJobId ? fallbackJob : null;
  }, [selectedJobId, jobs, sourceById, fallbackJob]);

  useEffect(() => {
    if (!selectedJobId || selectedJob || fallbackLoading) return;
    setFallbackLoading(true);
    adminApi.get(`/ops/crawl/jobs/${encodeURIComponent(selectedJobId)}`)
      .then(({ data }) => setFallbackJob(data))
      .catch((err) => {
        toast.error(errMsg(err, 'Could not find that crawl job'));
        setParams((prev) => { const n = new URLSearchParams(prev); n.delete('job'); return n; }, { replace: true });
      })
      .finally(() => setFallbackLoading(false));
  }, [selectedJobId, selectedJob, fallbackLoading, setParams]);

  const openJob = (jobId) => {
    setParams((prev) => { const n = new URLSearchParams(prev); n.set('job', jobId); return n; }, { replace: true });
  };
  const closeJob = () => {
    setFallbackJob(null);
    setParams((prev) => { const n = new URLSearchParams(prev); n.delete('job'); return n; }, { replace: true });
  };

  return (
    <div>
      <PageHeader
        title="Crawl"
        hint="Every source at once — for spotting a shared failure rather than chasing one brand."
        actions={canTrigger && (
          <button
            onClick={() => setForceOpen(true)}
            disabled={cooldown?.available === false}
            className="rounded-lg border border-status-critical/30 px-3 py-2 text-sm font-medium text-status-critical hover:bg-status-critical/10 disabled:opacity-40"
            title={cooldown?.available === false
              ? `On cooldown until ${formatDateTime(cooldown.available_at)}`
              : undefined}
          >
            Force crawl all
          </button>
        )}
      />

      <Band>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            { label: 'Sources', value: summary.total, tone: 'text-ink', filter: null },
            { label: 'Failing', value: summary.failing.length, tone: summary.failing.length ? 'text-status-critical' : 'text-ink', filter: 'failed' },
            { label: 'In flight', value: summary.running.length, tone: 'text-ink', filter: 'running' },
            { label: 'Never crawled', value: summary.never.length, tone: summary.never.length ? 'text-[#8a5a00]' : 'text-ink', filter: 'never' },
          ].map((s) => {
            const active = statusFilter === s.filter && s.filter !== null;
            return (
              <button
                key={s.label}
                type="button"
                onClick={() => setStatusFilter(active ? null : s.filter)}
                className={`rounded-xl border px-4 py-3 shadow-sm text-left w-full transition-colors ${
                  active
                    ? 'border-raspberry-400 bg-raspberry-50 ring-1 ring-raspberry-300'
                    : 'border-hairline bg-surface hover:bg-surface-page'
                } ${s.filter ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <div className="text-xs text-ink-muted">{s.label}</div>
                <div className={`text-2xl font-semibold tabular-nums ${s.tone}`}>{s.value}</div>
                {s.filter && s.value > 0 && (
                  <div className="mt-0.5 text-[10px] text-ink-muted">{active ? 'Click to clear' : 'Click to filter'}</div>
                )}
              </button>
            );
          })}
        </div>
        {summary.failing.length >= 3 && (
          <p className="mt-3 rounded-lg bg-status-critical/[0.06] px-3 py-2 text-xs leading-relaxed text-status-critical">
            {summary.failing.length} sources failed their last run. Several at once usually points at
            something shared — the queue, the worker, or a network path — rather than several brands
            changing their markup on the same night.
          </p>
        )}
        {cooldown?.available === false && (
          <p className="mt-3 text-xs text-ink-muted">
            Force-all is on cooldown until {formatDateTime(cooldown.available_at)} — last run{' '}
            {formatRelative(cooldown.last_triggered_at)}.
          </p>
        )}
      </Band>

      <Band
        title="Per-source status"
        actions={
          <input
            placeholder="Search by brand or source"
            value={sourceQuery}
            onChange={(e) => setSourceQuery(e.target.value)}
            className="w-56 rounded-lg border border-hairline px-2.5 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-raspberry-300"
          />
        }
      >
        {statusFilter && (
          <div className="mb-3 flex items-center gap-2">
            <span className="rounded-full bg-raspberry-100 px-2.5 py-0.5 text-[11px] font-medium text-raspberry-700">
              {{failed: 'Showing: Failing', never: 'Showing: Never crawled', running: 'Showing: In flight'}[statusFilter]}
            </span>
            <button
              type="button"
              onClick={() => setStatusFilter(null)}
              className="text-[11px] text-ink-muted hover:text-ink"
            >
              Clear ×
            </button>
          </div>
        )}
        <DataTable
          columns={statusColumns}
          rows={visibleStatus}
          rowKey={(s) => s.source_id}
          loading={loading}
          sort={sourceSort}
          onSort={onSourceSort}
          empty={
            <EmptyState
              title={statusFilter || sourceQuery ? 'No sources match that filter' : 'No crawl sources yet'}
              detail={statusFilter || sourceQuery ? undefined : 'Onboard a brand, or approve an application, to create one.'}
            />
          }
        />
        <Pagination
          meta={sourcePaginationMeta}
          onPage={setSourcePage}
          onPageSize={(n) => { setSourcePageSize(n); setSourcePage(1); }}
          loading={loading}
        />
      </Band>

      <Band
        title="Recent jobs"
        actions={
          <input
            placeholder="Search by brand, source or job id"
            value={jobQuery}
            onChange={(e) => setJobQuery(e.target.value)}
            className="w-64 rounded-lg border border-hairline px-2.5 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-raspberry-300"
          />
        }
      >
        <DataTable
          columns={jobColumns}
          rows={sortedJobs}
          rowKey={(j) => j.job_id}
          onRowClick={(j) => openJob(j.job_id)}
          sort={jobSort}
          onSort={onJobSort}
          dense
          maxHeight="28rem"
          empty={
            <EmptyState
              title={jobQuery ? 'No job matches that' : 'No jobs yet'}
              detail={jobQuery ? undefined : 'Nightly runs and manual triggers both appear here.'}
            />
          }
        />
        <p className="mt-2 text-[11px] text-ink-muted">Click a row for what it found, saved and deactivated.</p>
      </Band>

      <ConfirmDialog
        open={forceOpen}
        tone="critical"
        title="Force a crawl across every enabled source?"
        body={
          <>
            <p>
              This queues one job per enabled source — {summary.total} right now — and hits every brand's
              live website at once. It is rate-limited afterwards, so you will not be able to run it
              again for several hours.
            </p>
            <p className="mt-2">Use it when something systemic changed, not to refresh one brand.</p>
          </>
        }
        confirmLabel={`Queue ${summary.total} crawl jobs`}
        onConfirm={forceAll}
        onClose={() => setForceOpen(false)}
      />

      {selectedJobId && (
        <JobSummaryPanel job={selectedJob} loading={!selectedJob && fallbackLoading} onClose={closeJob} />
      )}
    </div>
  );
}
